/**
 * FinderColouringPage — printable colouring pages.
 *
 * SVG-based line art the kids can colour in on the app or print for
 * the car ride. Fully client-side. Tap a page to open the print view.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// ── Simple SVG line-art library. Each page is a self-contained SVG
// with white fills that print cleanly on A4.
const PAGES = [
  {
    key: "koala",
    title: "Koala on a gum tree",
    svg: (
      <g stroke="#0f172a" strokeWidth="3" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
        {/* Tree trunk */}
        <path d="M 150 500 Q 160 350 180 200 Q 200 100 250 60" fill="none" />
        <path d="M 200 500 Q 210 380 240 240" fill="none" />
        {/* Leaves */}
        <ellipse cx="280" cy="80" rx="60" ry="35" />
        <ellipse cx="340" cy="70" rx="40" ry="25" />
        <ellipse cx="270" cy="130" rx="45" ry="25" />
        {/* Koala body */}
        <ellipse cx="220" cy="320" rx="80" ry="100" />
        {/* Head */}
        <circle cx="220" cy="240" r="65" />
        {/* Ears */}
        <ellipse cx="170" cy="200" rx="30" ry="35" transform="rotate(-25 170 200)" />
        <ellipse cx="270" cy="200" rx="30" ry="35" transform="rotate(25 270 200)" />
        <ellipse cx="170" cy="205" rx="15" ry="20" transform="rotate(-25 170 205)" />
        <ellipse cx="270" cy="205" rx="15" ry="20" transform="rotate(25 270 205)" />
        {/* Face */}
        <ellipse cx="220" cy="270" rx="22" ry="18" fill="#0f172a" />
        <circle cx="200" cy="240" r="6" fill="#0f172a" />
        <circle cx="240" cy="240" r="6" fill="#0f172a" />
        {/* Arms */}
        <path d="M 150 300 Q 130 320 140 360" fill="#fff" />
        <path d="M 290 300 Q 310 320 300 360" fill="#fff" />
        {/* Legs */}
        <ellipse cx="180" cy="430" rx="30" ry="20" />
        <ellipse cx="260" cy="430" rx="30" ry="20" />
        {/* Ground line */}
        <path d="M 30 490 L 470 490" strokeWidth="2" />
      </g>
    ),
  },
  {
    key: "kangaroo",
    title: "Kangaroo & joey",
    svg: (
      <g stroke="#0f172a" strokeWidth="3" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
        {/* Body */}
        <path d="M 100 380 Q 100 260 200 250 Q 320 240 340 320 Q 355 380 340 420 L 380 460 L 340 470 L 300 430 L 180 430 L 140 470 L 110 470 L 130 420 Z" />
        {/* Head */}
        <ellipse cx="360" cy="220" rx="45" ry="55" />
        {/* Ears */}
        <path d="M 340 175 L 335 130 L 355 155 Z" />
        <path d="M 380 175 L 395 130 L 400 165 Z" />
        {/* Face */}
        <circle cx="370" cy="215" r="4" fill="#0f172a" />
        <path d="M 385 235 Q 395 240 385 250" fill="none" />
        {/* Pouch + Joey */}
        <ellipse cx="220" cy="360" rx="50" ry="35" />
        <circle cx="220" cy="345" r="20" />
        <path d="M 210 335 L 205 320 L 213 325 Z" />
        <path d="M 230 335 L 235 320 L 227 325 Z" />
        <circle cx="215" cy="345" r="2" fill="#0f172a" />
        <circle cx="225" cy="345" r="2" fill="#0f172a" />
        {/* Tail */}
        <path d="M 110 400 Q 40 430 30 470" fill="none" />
        {/* Ground */}
        <path d="M 20 480 L 480 480" strokeWidth="2" />
      </g>
    ),
  },
  {
    key: "beach",
    title: "Australian beach day",
    svg: (
      <g stroke="#0f172a" strokeWidth="3" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
        {/* Sun */}
        <circle cx="400" cy="90" r="40" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const rad = (a * Math.PI) / 180;
          const x1 = 400 + Math.cos(rad) * 50;
          const y1 = 90 + Math.sin(rad) * 50;
          const x2 = 400 + Math.cos(rad) * 68;
          const y2 = 90 + Math.sin(rad) * 68;
          return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        {/* Wave lines */}
        <path d="M 10 260 Q 100 240 200 260 T 490 260" fill="none" />
        <path d="M 10 290 Q 100 270 200 290 T 490 290" fill="none" />
        {/* Sand ripple */}
        <path d="M 10 480 L 490 480" strokeWidth="2" />
        <path d="M 30 460 Q 60 450 90 460" fill="none" />
        <path d="M 120 470 Q 150 460 180 470" fill="none" />
        <path d="M 320 460 Q 350 450 380 460" fill="none" />
        {/* Umbrella */}
        <path d="M 130 330 L 130 470" />
        <path d="M 60 330 Q 130 260 200 330" />
        <path d="M 60 330 L 130 340 L 200 330" />
        <path d="M 90 330 L 130 340 L 170 330" />
        {/* Sandcastle */}
        <rect x="260" y="380" width="120" height="80" />
        <rect x="240" y="360" width="30" height="100" />
        <rect x="370" y="360" width="30" height="100" />
        <polygon points="255,360 240,340 270,340" />
        <polygon points="385,360 370,340 400,340" />
        <path d="M 310 380 L 310 420 L 330 420 L 330 380" fill="none" />
        {/* Seagull */}
        <path d="M 250 140 Q 265 125 280 140 Q 295 125 310 140" fill="none" />
      </g>
    ),
  },
  {
    key: "outback",
    title: "Outback sunset",
    svg: (
      <g stroke="#0f172a" strokeWidth="3" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
        {/* Sun */}
        <circle cx="250" cy="180" r="55" />
        {/* Uluru silhouette (front) */}
        <path d="M 30 400 Q 120 300 250 320 Q 380 340 470 400 L 470 470 L 30 470 Z" />
        {/* Cracks on rock */}
        <path d="M 120 380 L 130 420" fill="none" />
        <path d="M 200 350 Q 220 400 200 450" fill="none" />
        <path d="M 320 360 L 320 460" fill="none" />
        {/* Distant tree */}
        <path d="M 100 470 L 100 420" />
        <ellipse cx="100" cy="410" rx="20" ry="12" />
        <ellipse cx="90"  cy="400" rx="12" ry="8" />
        <ellipse cx="115" cy="400" rx="12" ry="8" />
        {/* Kangaroo silhouette */}
        <path d="M 400 460 Q 400 420 420 415 L 430 410 L 435 405 L 440 415 L 440 425 L 430 425 L 430 460 M 420 460 L 415 470 Q 400 465 400 460" fill="#fff" />
        {/* Horizon line */}
        <path d="M 30 400 L 470 400" strokeWidth="1" strokeDasharray="4 6" />
        {/* Ground base */}
        <path d="M 30 470 L 470 470" strokeWidth="2" />
      </g>
    ),
  },
  {
    key: "car",
    title: "Road trip car",
    svg: (
      <g stroke="#0f172a" strokeWidth="3" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
        {/* Road */}
        <path d="M 20 440 L 480 440" strokeWidth="2" />
        <path d="M 100 440 L 160 440 M 220 440 L 280 440 M 340 440 L 400 440" strokeDasharray="8 4" />
        {/* Body */}
        <path d="M 60 400 L 90 340 Q 100 330 120 330 L 200 330 L 220 300 L 340 300 L 380 340 L 430 340 Q 445 340 445 355 L 445 400 Z" />
        {/* Roof rack + luggage */}
        <rect x="140" y="270" width="200" height="30" />
        <line x1="180" y1="270" x2="180" y2="300" />
        <line x1="240" y1="270" x2="240" y2="300" />
        <line x1="300" y1="270" x2="300" y2="300" />
        {/* Windows */}
        <path d="M 110 340 L 200 340 L 200 330 L 130 330 Z" />
        <path d="M 220 340 L 220 305 L 320 305 L 340 340 Z" />
        {/* Wheels */}
        <circle cx="140" cy="405" r="30" />
        <circle cx="140" cy="405" r="12" />
        <circle cx="360" cy="405" r="30" />
        <circle cx="360" cy="405" r="12" />
        {/* Headlight */}
        <circle cx="420" cy="365" r="8" />
        {/* Cloud */}
        <ellipse cx="120" cy="120" rx="45" ry="20" />
        <ellipse cx="90"  cy="115" rx="25" ry="15" />
        <ellipse cx="150" cy="110" rx="30" ry="18" />
        <ellipse cx="360" cy="140" rx="55" ry="22" />
        <ellipse cx="330" cy="130" rx="30" ry="18" />
      </g>
    ),
  },
  {
    key: "fish",
    title: "Great Barrier fish",
    svg: (
      <g stroke="#0f172a" strokeWidth="3" fill="#fff" strokeLinejoin="round" strokeLinecap="round">
        {/* Big fish */}
        <path d="M 100 250 Q 150 180 260 200 Q 360 220 390 260 Q 360 300 260 320 Q 150 340 100 270 L 60 300 L 60 220 Z" />
        {/* Eye */}
        <circle cx="150" cy="240" r="10" />
        <circle cx="150" cy="240" r="4" fill="#0f172a" />
        {/* Fins */}
        <path d="M 220 200 L 240 170 L 260 200" fill="#fff" />
        <path d="M 220 320 L 240 350 L 260 320" fill="#fff" />
        {/* Stripes */}
        <path d="M 200 210 Q 210 260 200 310" fill="none" />
        <path d="M 260 210 Q 270 260 260 310" fill="none" />
        <path d="M 320 220 Q 330 260 320 300" fill="none" />
        {/* Bubbles */}
        <circle cx="90"  cy="150" r="8" />
        <circle cx="120" cy="120" r="12" />
        <circle cx="80"  cy="100" r="6" />
        {/* Small fish */}
        <path d="M 380 400 Q 410 380 440 400 Q 460 415 440 425 Q 410 440 380 425 L 365 435 L 365 395 Z" />
        <circle cx="405" cy="405" r="3" fill="#0f172a" />
        {/* Seaweed */}
        <path d="M 40 480 Q 30 430 50 400 Q 30 360 50 320" fill="none" />
        <path d="M 470 480 Q 460 430 475 400 Q 460 360 475 320" fill="none" />
        {/* Sand */}
        <path d="M 10 470 L 490 470" strokeWidth="2" />
      </g>
    ),
  },
];

export default function FinderColouringPage() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    document.title = active
      ? `${active.title} — Colouring | HustleFinder`
      : "Colouring pages — HustleFinder Kids";
  }, [active]);

  if (active) {
    return (
      <div
        data-testid="colouring-detail"
        className="min-h-screen bg-white pb-8"
      >
        <style>{`
          @media print {
            .no-print { display: none !important; }
            body { background: #fff; }
          }
        `}</style>
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="no-print flex items-center justify-between mb-4">
            <button
              onClick={() => setActive(null)}
              data-testid="colouring-back-btn"
              className="text-xs text-emerald-700 hover:underline"
            >
              ← back to colouring pages
            </button>
            <button
              onClick={() => window.print()}
              data-testid="colouring-print-btn"
              className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full hover:bg-slate-800"
            >
              Print A4
            </button>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3 text-center">
            {active.title}
          </h1>
          <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
            <svg
              viewBox="0 0 500 500"
              xmlns="http://www.w3.org/2000/svg"
              data-testid={`colouring-svg-${active.key}`}
              className="w-full h-auto"
            >
              {active.svg}
            </svg>
          </div>
          <p className="no-print text-center text-xs text-slate-500 mt-4">
            Tip: print in landscape or portrait — the line art scales
            cleanly to A4.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="colouring-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-16"
    >
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link
          to="/finder/kids"
          data-testid="colouring-back-link"
          className="text-xs text-emerald-700 hover:underline"
        >
          ← back to Kid Zone
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 mt-3 mb-1">
          Colouring pages
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          Aussie-themed line art. Tap any page to print A4 for the road,
          or colour in on the tablet.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {PAGES.map((p) => (
            <button
              key={p.key}
              onClick={() => setActive(p)}
              data-testid={`colouring-card-${p.key}`}
              className="text-left bg-white border border-slate-200 rounded-2xl p-3
                          hover:border-emerald-400 hover:shadow-md transition"
            >
              <div className="aspect-square bg-slate-50 rounded-lg mb-2 overflow-hidden flex items-center justify-center">
                <svg viewBox="0 0 500 500" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  {p.svg}
                </svg>
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {p.title}
              </div>
              <div className="text-[11px] text-emerald-700 mt-1">
                Print A4 →
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
